import {
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { preprocessFile } from "./engine/preprocess";
import {
  ensureAccessoryAssets,
  ensureTemplateAssets,
  exportProject,
  outputSizes,
  renderProject,
  warmTemplateAssets,
} from "./engine/render";
import {
  accessoryGroupsForSlot,
  accessoryLabel,
  decorationSlotLabels,
} from "./engine/accessories";
import {
  persistPreparedProject,
  persistSettings,
  restorePreparedSession,
} from "./engine/session";
import {
  decorationSlots,
  defaultDecorations,
  templateById,
  templates,
} from "./engine/templates";
import type {
  AccessoryId,
  DecorationSlot,
  DecorationTransforms,
  EditSettings,
  NameMode,
  PreparedProject,
  ProgressUpdate,
  TemplateId,
  Transform,
} from "./types";

const identityTransform = (): Transform => ({ x: 0, y: 0, scale: 1, angle: 0, flipX: false });
const decorationTransformsFor = (count: number): DecorationTransforms => ({
  head: Array.from({ length: count }, identityTransform),
  face: Array.from({ length: count }, identityTransform),
  behind: Array.from({ length: count }, identityTransform),
  card: Array.from({ length: count }, identityTransform),
});
type AdjustTarget = "hero" | DecorationSlot;
type EditorPanel = "style" | "privacy" | "adjust" | "layout";
type FeedbackKind = "bug" | "idea" | "design";

function settingsFor(count: number): EditSettings {
  const seed = Math.floor(Math.random() * 1000);
  return {
    ratio: "4:5",
    template: "cute",
    composition: "top",
    nameModes: Array.from({ length: count }, (_, index) => index === 0 ? "show" : "mask"),
    decorations: defaultDecorations("cute", count, seed),
    heroTransforms: Array.from({ length: count }, identityTransform),
    decorationTransforms: decorationTransformsFor(count),
    plateAngles: [...templateById("cute").angles].slice(0, count),
    gap: templateById("cute").gap,
    seed,
  };
}

function replaceAt<T>(items: T[], index: number, value: T) {
  return items.map((item, itemIndex) => itemIndex === index ? value : item);
}

function transformAt(items: Transform[], index: number, change: Partial<Transform>) {
  const current = items[index] ?? identityTransform();
  return replaceAt(items, index, {
    x: Math.max(-0.35, Math.min(0.35, change.x ?? current.x)),
    y: Math.max(-0.35, Math.min(0.35, change.y ?? current.y)),
    scale: Math.max(0.65, Math.min(1.5, change.scale ?? current.scale)),
    angle: Math.max(-20, Math.min(20, change.angle ?? current.angle)),
    flipX: change.flipX ?? current.flipX,
  });
}

export default function App() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const demoStartedRef = useRef(false);
  const [project, setProject] = useState<PreparedProject | null>(null);
  const [settings, setSettings] = useState<EditSettings>(() => settingsFor(2));
  const [phase, setPhase] = useState<"idle" | "preparing" | "editing" | "exporting">("idle");
  const [progress, setProgress] = useState<ProgressUpdate>({
    stage: "decode",
    progress: 0,
    message: "",
  });
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [selectedMember, setSelectedMember] = useState(0);
  const [dragTarget, setDragTarget] = useState<AdjustTarget>("head");
  const [adjustMode, setAdjustMode] = useState(false);
  const [activePanel, setActivePanel] = useState<EditorPanel>("style");
  const [exportComplete, setExportComplete] = useState(false);
  const [inviteFeedback, setInviteFeedback] = useState("");
  const [assetRevision, setAssetRevision] = useState(0);
  const [feedbackAvailable, setFeedbackAvailable] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackKind, setFeedbackKind] = useState<FeedbackKind>("bug");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackContact, setFeedbackContact] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  useEffect(() => {
    if (!project || !previewRef.current) return;
    const frame = requestAnimationFrame(() => {
      if (!previewRef.current) return;
      // Keep the editor crisp on high-density phones without rendering the
      // full export size on every small adjustment.
      const cssWidth = previewRef.current.clientWidth || 540;
      const densityScale = cssWidth * Math.min(window.devicePixelRatio || 1, 2.2) / 1080;
      renderProject(previewRef.current, project, settings, Math.min(0.72, Math.max(0.5, densityScale)));
    });
    return () => cancelAnimationFrame(frame);
  }, [assetRevision, project, settings]);

  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    void ensureTemplateAssets(settings.template)
      .then(() => {
        if (!cancelled) setAssetRevision((revision) => revision + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [project, settings.template]);

  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    const ids = Object.values(settings.decorations).flat();
    void ensureAccessoryAssets(ids)
      .then(() => {
        if (!cancelled) setAssetRevision((revision) => revision + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [project, settings.decorations]);

  useEffect(() => {
    if (
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
      && new URLSearchParams(window.location.search).get("feedback") === "preview"
    ) {
      setFeedbackAvailable(true);
      return;
    }
    const controller = new AbortController();
    void fetch("/api/feedback", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return;
        const result = await response.json() as { configured?: boolean };
        setFeedbackAvailable(result.configured === true);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!feedbackOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFeedbackOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [feedbackOpen]);

  const processFile = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("PNG・JPEG・WebPのスクリーンショットを選んでください。");
      return;
    }
    setError("");
    setPhase("preparing");
    setProgress({ stage: "decode", progress: 0, message: "準備しています…" });
    try {
      const [nextProject] = await Promise.all([
        preprocessFile(file, setProgress),
        ensureTemplateAssets("cute"),
      ]);
      const nextSettings = settingsFor(nextProject.cards.length);
      setProject(nextProject);
      setSettings(nextSettings);
      setSelectedMember(0);
      setAdjustMode(false);
      setActivePanel("style");
      setExportComplete(false);
      setInviteFeedback("");
      setPhase("editing");
      warmTemplateAssets("cute");
      persistSettings(nextSettings);
      void persistPreparedProject(nextProject).catch(() => undefined);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "スクリーンショットを処理できませんでした");
      setPhase("idle");
    }
  };

  useEffect(() => {
    if (demoStartedRef.current) return;
    const sample = new URLSearchParams(window.location.search).get("sample");
    demoStartedRef.current = true;
    if (sample && /^[\w.-]+$/.test(sample)) {
      void fetch(`/demo/${sample}`)
        .then(async (response) => {
          if (!response.ok) throw new Error("開発用サンプルを読み込めませんでした");
          const blob = await response.blob();
          await processFile(new File([blob], sample, { type: blob.type || "image/png" }));
        })
        .catch((caught) => {
          setError(caught instanceof Error ? caught.message : "開発用サンプルを読み込めませんでした");
        });
      return;
    }
    setPhase("preparing");
    setProgress({ stage: "layers", progress: 0.6, message: "前回の編集を戻しています…" });
    void restorePreparedSession()
      .then(async (restored) => {
        if (!restored) {
          setPhase("idle");
          return;
        }
        const restoredSettings = restored.settings ?? settingsFor(restored.project.cards.length);
        await ensureTemplateAssets(restoredSettings.template);
        setProject(restored.project);
        setSettings(restoredSettings);
        setPhase("editing");
        warmTemplateAssets(restoredSettings.template);
      })
      .catch(() => setPhase("idle"));
  }, []);

  useEffect(() => {
    if (!project || phase !== "editing") return;
    const timeout = globalThis.setTimeout(() => persistSettings(settings), 180);
    return () => globalThis.clearTimeout(timeout);
  }, [phase, project, settings]);

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    void processFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    void processFile(event.dataTransfer.files[0]);
  };

  const changeTemplate = (template: TemplateId) => {
    const selected = templateById(template);
    setSettings((current) => ({
      ...current,
      template,
      gap: selected.gap,
      plateAngles: Array.from(
        { length: project?.cards.length ?? current.plateAngles.length },
        (_, index) => selected.angles[index] ?? 0,
      ),
      decorations: defaultDecorations(template, project?.cards.length ?? current.heroTransforms.length, current.seed),
    }));
  };

  const setNameMode = (index: number, mode: NameMode) => {
    setSettings((current) => ({ ...current, nameModes: replaceAt(current.nameModes, index, mode) }));
  };

  const setDecoration = (index: number, slot: DecorationSlot, accessory: AccessoryId) => {
    setSettings((current) => ({
      ...current,
      decorations: {
        ...current.decorations,
        [slot]: replaceAt(current.decorations[slot], index, accessory),
      },
    }));
  };

  const adjustSelected = (change: Partial<Transform>) => {
    setSettings((current) => {
      if (dragTarget === "hero") {
        return {
          ...current,
          heroTransforms: transformAt(current.heroTransforms, selectedMember, change),
        };
      }
      return {
        ...current,
        decorationTransforms: {
          ...current.decorationTransforms,
          [dragTarget]: transformAt(current.decorationTransforms[dragTarget], selectedMember, change),
        },
      };
    });
  };

  const selectedTransform = dragTarget === "hero"
    ? settings.heroTransforms[selectedMember] ?? identityTransform()
    : settings.decorationTransforms[dragTarget][selectedMember] ?? identityTransform();

  const resetSelected = () => adjustSelected(identityTransform());

  const anotherVariation = () => {
    setSettings((current) => {
      const seed = current.seed + 1;
      return {
        ...current,
        seed,
        decorations: defaultDecorations(current.template, project?.cards.length ?? current.heroTransforms.length, seed),
        decorationTransforms: decorationTransformsFor(project?.cards.length ?? current.heroTransforms.length),
      };
    });
  };

  const save = async () => {
    if (!project) return;
    setPhase("exporting");
    setError("");
    try {
      const blob = await exportProject(project, settings);
      const fileName = `kotodeco-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: "image/png" });
      const sharing = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
      };
      if (navigator.share && sharing.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "こてデコ" });
          setExportComplete(true);
          return;
        } catch (caught) {
          if (caught instanceof DOMException && caught.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      globalThis.setTimeout(() => URL.revokeObjectURL(url), 2000);
      setExportComplete(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "完成画像を保存できませんでした");
    } finally {
      setPhase("editing");
    }
  };

  const shareInvite = async () => {
    const text = "固定スクショ、これで仕上げた。まだ知ってる人少ないかも。";
    const url = window.location.origin;
    const copyText = `${text}\n${url}`;
    setInviteFeedback("");
    try {
      if (navigator.share) {
        await navigator.share({ title: "こてデコ", text, url });
        setInviteFeedback("送れました");
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyText);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = copyText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.append(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        textarea.remove();
        if (!copied) throw new Error("copy failed");
      }
      setInviteFeedback("リンクをコピーしました");
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setInviteFeedback("リンクをコピーできませんでした");
    }
  };

  const submitFeedback = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const trimmed = feedbackMessage.trim();
    if (trimmed.length < 8 || feedbackStatus === "sending") return;
    setFeedbackStatus("sending");
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: feedbackKind,
          message: trimmed,
          contact: feedbackContact.trim(),
          website: String(formData.get("website") ?? ""),
          context: {
            page: window.location.pathname,
            screen: `${window.screen.width}x${window.screen.height}`,
            cards: project?.cards.length ?? null,
            theme: project ? settings.template : null,
            model: project?.modelMode ?? null,
          },
        }),
      });
      if (!response.ok) throw new Error("send failed");
      setFeedbackStatus("sent");
      setFeedbackMessage("");
      setFeedbackContact("");
    } catch {
      setFeedbackStatus("error");
    }
  };

  const pointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!project || !adjustMode) return;
    event.preventDefault();
    pointerRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const pointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!adjustMode || !pointerRef.current || !project) return;
    event.preventDefault();
    const previous = pointerRef.current;
    const bounds = event.currentTarget.getBoundingClientRect();
    const dx = (event.clientX - previous.x) / bounds.width;
    const dy = (event.clientY - previous.y) / (bounds.width / 3.2);
    pointerRef.current = { x: event.clientX, y: event.clientY };
    setSettings((current) => {
      const selected = dragTarget === "hero"
        ? current.heroTransforms[selectedMember] ?? identityTransform()
        : current.decorationTransforms[dragTarget][selectedMember] ?? identityTransform();
      const next = {
        x: selected.x + dx,
        y: selected.y + dy,
      };
      if (dragTarget === "hero") {
        return {
          ...current,
          heroTransforms: transformAt(current.heroTransforms, selectedMember, next),
        };
      }
      return {
        ...current,
        decorationTransforms: {
          ...current.decorationTransforms,
          [dragTarget]: transformAt(current.decorationTransforms[dragTarget], selectedMember, next),
        },
      };
    });
  };

  const pointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    pointerRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const output = outputSizes[settings.ratio];
  const targetLabel = dragTarget === "hero" ? "キャラ" : decorationSlotLabels[dragTarget];

  return (
    <main
      className="app-shell"
      data-phase={phase}
      data-model-mode={project?.modelMode ?? ""}
    >
      <header className="masthead">
        <div className="brand-mark" aria-hidden="true"><span /><span /><i>✦</i></div>
        <div className="brand-copy">
          <h1>こてデコ</h1>
          <span>固定スクショを、もっとかわいく。</span>
        </div>
        {project && <span className="privacy-pill">画像はこの端末だけ</span>}
      </header>

      {phase === "idle" && !project && (
        <section
          className={`landing ${dragging ? "is-dragging" : ""}`}
          onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <button type="button" className="drop-card" onClick={() => fileInputRef.current?.click()}>
            <span className="drop-art" aria-hidden="true">
              <i /><i /><i /><b>✦</b>
            </span>
            <strong>募集スクショを選ぶ</strong>
            <small>写真ライブラリから選択</small>
          </button>
          <p className="landing-privacy"><span aria-hidden="true">●</span> 画像は端末の外へ送信されません</p>
          {error && <p className="error-message" role="alert">{error}</p>}
        </section>
      )}

      {phase === "preparing" && (
        <section className="preparing-card" aria-live="polite">
          <div className="progress-orbit"><span /><i>✦</i></div>
          <p className="eyebrow">スクショを仕上げています</p>
          <h2>{progress.message}</h2>
          <div className="progress-track"><span style={{ width: `${Math.max(3, progress.progress * 100)}%` }} /></div>
          <strong>{Math.round(progress.progress * 100)}%</strong>
          <p>終わったら、そのまま仕上がりを表示します。</p>
        </section>
      )}

      {project && phase !== "preparing" && (
        <section className="workspace">
          <div className="preview-column">
            <div className="preview-topline">
              <span>{project.cards.length}人固定</span>
              <button type="button" onClick={() => fileInputRef.current?.click()}>別のスクショ</button>
            </div>
            <div
              className={`canvas-stage ${adjustMode ? "is-adjusting" : ""}`}
              style={{ aspectRatio: `${output.width} / ${output.height}` }}
            >
              <canvas
                ref={previewRef}
                aria-label={adjustMode
                  ? `加工後のプレビュー。${selectedMember + 1}人目の${targetLabel}をドラッグして移動できます`
                  : "加工後のプレビュー"}
                onPointerDown={pointerDown}
                onPointerMove={pointerMove}
                onPointerUp={pointerUp}
                onPointerCancel={pointerUp}
              />
              {adjustMode && (
                <div className="drag-hint">{selectedMember + 1}人目の{targetLabel}をドラッグ</div>
              )}
            </div>
            <div className={`preview-adjust ${adjustMode ? "is-open" : ""}`}>
              {!adjustMode ? (
                <button type="button" className="adjust-trigger" onClick={() => setAdjustMode(true)}>
                  位置を微調整
                </button>
              ) : (
                <>
                  <div className="adjust-title">
                    <strong>位置を微調整</strong>
                    <button type="button" onClick={() => setAdjustMode(false)}>完了</button>
                  </div>
                  <div className="adjust-tools">
                    <div className="member-pills" aria-label="調整する人">
                      {project.cards.map((_, index) => (
                        <button
                          type="button"
                          key={index}
                          className={selectedMember === index ? "is-selected" : ""}
                          onClick={() => setSelectedMember(index)}
                        >{index + 1}人目</button>
                      ))}
                    </div>
                    <div className="target-toggle target-toggle-layers">
                      <button type="button" className={dragTarget === "hero" ? "is-selected" : ""} onClick={() => setDragTarget("hero")}>キャラ</button>
                      {decorationSlots.map((slot) => (
                        <button type="button" key={slot} className={dragTarget === slot ? "is-selected" : ""} onClick={() => setDragTarget(slot)}>
                          {decorationSlotLabels[slot]}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <aside className="controls">
            <nav className="inspector-tabs" aria-label="編集メニュー">
              {([
                ["style", "テーマ"],
                ["privacy", "名前"],
                ["adjust", "調整"],
                ["layout", "サイズ"],
              ] as Array<[EditorPanel, string]>).map(([panel, label]) => (
                <button
                  type="button"
                  key={panel}
                  aria-current={activePanel === panel ? "page" : undefined}
                  className={activePanel === panel ? "is-selected" : ""}
                  onClick={() => setActivePanel(panel)}
                >{label}</button>
              ))}
            </nav>

            {activePanel === "style" && (
              <section className="control-section inspector-panel">
                <div className="section-heading">
                  <div>
                    <h3>仕上がりを選ぶ</h3>
                  </div>
                  <button type="button" className="variation-action" onClick={anotherVariation}>デコを変える</button>
                </div>
                <div className="theme-scroller">
                  {templates.map((template) => (
                    <button
                      type="button"
                      key={template.id}
                      className={settings.template === template.id ? "is-selected" : ""}
                      style={{
                        "--theme-surface": template.surface,
                        "--theme-ink": template.ink,
                      } as CSSProperties}
                      onClick={() => changeTemplate(template.id)}
                    >
                      <span className="theme-preview" aria-hidden="true"><i /><i /></span>
                      <span><strong>{template.name}</strong><small>{template.detail}</small></span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {activePanel === "privacy" && (
              <section className="control-section inspector-panel">
                <div className="section-heading">
                  <div><h3>名前の見せ方</h3></div>
                </div>
                <div className="member-privacy-list">
                  {project.cards.map((_, index) => (
                    <div className="privacy-row" key={index}>
                      <button type="button" className="member-label" onClick={() => setSelectedMember(index)}>
                        <span>{index + 1}</span>{index + 1}人目
                      </button>
                      <div className="segmented">
                        {([
                          ["show", "見せる"],
                          ["mask", "デコで隠す"],
                          ["hide", "余白にする"],
                        ] as Array<[NameMode, string]>).map(([mode, label]) => (
                          <button
                            type="button"
                            key={mode}
                            className={settings.nameModes[index] === mode ? "is-selected" : ""}
                            onClick={() => setNameMode(index, mode)}
                          >{label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activePanel === "adjust" && (
              <section className="control-section inspector-panel live-adjuster">
                <div className="section-heading">
                  <div><h3>{selectedMember + 1}人目を微調整</h3></div>
                </div>
                <div className="member-pills member-switcher" aria-label="調整する人">
                  {project.cards.map((_, index) => (
                    <button type="button" key={index} className={selectedMember === index ? "is-selected" : ""} onClick={() => setSelectedMember(index)}>
                      {index + 1}人目
                    </button>
                  ))}
                </div>
                <div className="target-toggle target-toggle-layers adjust-layer-tabs" aria-label="調整するレイヤー">
                  <button type="button" className={dragTarget === "hero" ? "is-selected" : ""} onClick={() => setDragTarget("hero")}>キャラ</button>
                  {decorationSlots.map((slot) => (
                    <button type="button" key={slot} className={dragTarget === slot ? "is-selected" : ""} onClick={() => setDragTarget(slot)}>
                      {decorationSlotLabels[slot]}
                    </button>
                  ))}
                </div>
                {dragTarget !== "hero" && (
                  <label className="select-control">
                    <span>{decorationSlotLabels[dragTarget]}につけるもの</span>
                    <select
                      value={settings.decorations[dragTarget][selectedMember] ?? "none"}
                      onChange={(event) => setDecoration(selectedMember, dragTarget, event.target.value as AccessoryId)}
                    >
                      <option value="none">デコなし</option>
                      {accessoryGroupsForSlot(dragTarget).map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.ids.map((id) => (
                            <option key={id} value={id}>{accessoryLabel(id)}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                )}
                <div className="nudge-grid" aria-label={`${targetLabel}を移動`}>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x - 0.025, y: selectedTransform.y - 0.025 })}>↖</button>
                  <button type="button" onClick={() => adjustSelected({ y: selectedTransform.y - 0.025 })}>↑</button>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x + 0.025, y: selectedTransform.y - 0.025 })}>↗</button>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x - 0.025 })}>←</button>
                  <button type="button" className="reset-dot" onClick={resetSelected}>●</button>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x + 0.025 })}>→</button>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x - 0.025, y: selectedTransform.y + 0.025 })}>↙</button>
                  <button type="button" onClick={() => adjustSelected({ y: selectedTransform.y + 0.025 })}>↓</button>
                  <button type="button" onClick={() => adjustSelected({ x: selectedTransform.x + 0.025, y: selectedTransform.y + 0.025 })}>↘</button>
                </div>
                <div className="transform-row">
                  <button type="button" onClick={() => adjustSelected({ scale: selectedTransform.scale - 0.06 })}>− 小さく</button>
                  <button type="button" onClick={() => adjustSelected({ scale: selectedTransform.scale + 0.06 })}>＋ 大きく</button>
                  <button type="button" aria-label="左に回転" onClick={() => adjustSelected({ angle: selectedTransform.angle - 2.5 })}>↶</button>
                  <button type="button" aria-label="右に回転" onClick={() => adjustSelected({ angle: selectedTransform.angle + 2.5 })}>↷</button>
                </div>
                {dragTarget !== "hero" && (
                  <button
                    type="button"
                    className={`flip-action ${selectedTransform.flipX ? "is-selected" : ""}`}
                    aria-pressed={selectedTransform.flipX}
                    onClick={() => adjustSelected({ flipX: !selectedTransform.flipX })}
                  >
                    ↔ 左右を反転
                  </button>
                )}
              </section>
            )}

            {activePanel === "layout" && (
              <section className="control-section inspector-panel">
                <div className="section-heading">
                  <div><h3>投稿サイズ</h3></div>
                </div>
                <div className="preset-row">
                  {([
                    ["top", "自然に飛び出す"],
                    ["bold", "大きく見せる"],
                    ["flat", "プレート内"],
                  ] as const).map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={settings.composition === value ? "is-selected" : ""}
                      onClick={() => setSettings((current) => ({ ...current, composition: value }))}
                    >{label}</button>
                  ))}
                </div>
                <div className="two-selects">
                  <label className="select-control">
                    <span>画像の比率</span>
                    <select value={settings.ratio} onChange={(event) => setSettings((current) => ({ ...current, ratio: event.target.value as EditSettings["ratio"] }))}>
                      <option value="4:5">4:5 投稿向け</option>
                      <option value="1:1">1:1 正方形</option>
                      <option value="9:16">9:16 ストーリー</option>
                    </select>
                  </label>
                  <label className="range-control">
                    <span>カード間隔</span>
                    <input
                      type="range"
                      min="0.002"
                      max="0.04"
                      step="0.001"
                      value={settings.gap}
                      onChange={(event) => setSettings((current) => ({ ...current, gap: Number(event.target.value) }))}
                    />
                  </label>
                </div>
              </section>
            )}

            <div className="action-stack">
              <button type="button" className="primary-action" disabled={phase === "exporting"} onClick={() => void save()}>
                <span aria-hidden="true">↑</span>
                {phase === "exporting" ? "画像を作成中…" : "保存・共有"}
              </button>
            </div>
            {exportComplete && (
              <section className="completion-card" aria-live="polite">
                <div className="completion-check" aria-hidden="true">✓</div>
                <div>
                  <strong>できました</strong>
                  <p>このまま投稿できます。気に入ったら、仲のいい人にも。</p>
                </div>
                <div className="completion-actions">
                  <button type="button" onClick={() => fileInputRef.current?.click()}>別のスクショ</button>
                  <button type="button" onClick={() => void shareInvite()}>こっそり教える</button>
                </div>
                {inviteFeedback && <small>{inviteFeedback}</small>}
              </section>
            )}
            {error && <p className="error-message" role="alert">{error}</p>}
          </aside>
        </section>
      )}

      <input ref={fileInputRef} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} />

      {feedbackAvailable && (
        <footer className="quiet-footer">
          <button
            type="button"
            onClick={() => {
              setFeedbackStatus("idle");
              setFeedbackOpen(true);
            }}
          >
            ご意見・不具合を送る
          </button>
        </footer>
      )}

      {feedbackOpen && (
        <div className="feedback-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setFeedbackOpen(false);
        }}>
          <section className="feedback-sheet" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
            <button type="button" className="feedback-close" aria-label="閉じる" onClick={() => setFeedbackOpen(false)}>×</button>
            {feedbackStatus === "sent" ? (
              <div className="feedback-thanks" aria-live="polite">
                <span aria-hidden="true">✓</span>
                <h2 id="feedback-title">届きました</h2>
                <p>教えてくれてありがとう。次の改善に活かします。</p>
                <button type="button" onClick={() => setFeedbackOpen(false)}>閉じる</button>
              </div>
            ) : (
              <form onSubmit={(event) => void submitFeedback(event)}>
                <p className="eyebrow">FEEDBACK</p>
                <h2 id="feedback-title">気づいたことを教えてください</h2>
                <p className="feedback-lead">画像は送信されません。端末の種類と選んだテーマだけ、改善の手がかりとして添えます。</p>
                <div className="feedback-kinds" aria-label="内容の種類">
                  {([
                    ["bug", "うまく動かない"],
                    ["idea", "こんなのがほしい"],
                    ["design", "仕上がりについて"],
                  ] as Array<[FeedbackKind, string]>).map(([kind, label]) => (
                    <button
                      type="button"
                      key={kind}
                      className={feedbackKind === kind ? "is-selected" : ""}
                      onClick={() => setFeedbackKind(kind)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label className="feedback-field">
                  <span>内容</span>
                  <textarea
                    required
                    minLength={8}
                    maxLength={1200}
                    rows={6}
                    value={feedbackMessage}
                    placeholder="どの画面で、どうなったか。短くても大丈夫です。"
                    onChange={(event) => {
                      setFeedbackMessage(event.target.value);
                      if (feedbackStatus === "error") setFeedbackStatus("idle");
                    }}
                  />
                  <small>{feedbackMessage.length} / 1200</small>
                </label>
                <label className="feedback-field">
                  <span>返信先 <small>任意</small></span>
                  <input
                    type="text"
                    maxLength={160}
                    value={feedbackContact}
                    placeholder="XのID またはメールアドレス"
                    onChange={(event) => setFeedbackContact(event.target.value)}
                  />
                </label>
                <input className="feedback-honeypot" type="text" name="website" tabIndex={-1} autoComplete="off" />
                {feedbackStatus === "error" && <p className="feedback-error" role="alert">送れませんでした。少し待って、もう一度お試しください。</p>}
                <button
                  type="submit"
                  className="feedback-submit"
                  disabled={feedbackMessage.trim().length < 8 || feedbackStatus === "sending"}
                >
                  {feedbackStatus === "sending" ? "送信中…" : "送信する"}
                </button>
              </form>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
